import type { FacetValue } from '@atlas/types'
import type {
	Article,
	ArticleListParams,
	ArticleStatus,
	ArticleTranslationVariant,
	CreateArticleInput,
	UpdateArticleInput,
} from '@/domain/article.types'
import { apiClient } from './api.client'

/** Article API client — thin `apiClient` wrappers, no business logic, no error translation. */
const unwrap = <T>(data: { data: T }): T => data.data

/**
 * Split the list params the way the backend reads them: `status`,
 * `articleType` and each classification axis as their own comma-separated
 * `filters[…]` parameters, and the free-text `search` plus `languageIso` inside
 * the legacy JSON `filter` parameter that `parseQueryParams` unpacks.
 *
 * A comma-joined string rather than an array is deliberate: Axios would
 * otherwise serialize an array as `filters[status][]=…`, which the backend does
 * not read.
 */
export function articleListQueryParams(params: ArticleListParams = {}): Record<string, string | number> {
	const query: Record<string, string | number> = {}

	if (params.status && params.status.length > 0) query['filters[status]'] = params.status.join(',')
	if (params.articleType && params.articleType.length > 0) {
		query['filters[articleType]'] = params.articleType.join(',')
	}
	for (const [axisCode, values] of Object.entries(params.classification ?? {})) {
		if (values && values.length > 0) query[`filters[${axisCode}]`] = values.join(',')
	}

	const jsonFilter: Record<string, string> = {}
	if (params.search) jsonFilter.search = params.search
	if (params.languageIso) jsonFilter.languageIso = params.languageIso
	if (Object.keys(jsonFilter).length > 0) query.filter = JSON.stringify(jsonFilter)

	if (params.page !== undefined) query.page = params.page
	if (params.limit !== undefined) query.limit = params.limit
	if (params.sort) query.sort = params.sort
	if (params.order) query.order = params.order

	return query
}

export const articleApi = {
	/**
	 * `GET /articles` answers 204 with no body when nothing matches, which Axios
	 * surfaces as an empty string rather than a `{ data }` envelope — normalized
	 * to `[]` here so no caller has to know that.
	 */
	list: (params: ArticleListParams = {}): Promise<Article[]> =>
		apiClient
			.get<{ data: Article[] } | ''>('/articles', { params: articleListQueryParams(params) })
			.then((r) => (r.status === 204 || !r.data ? [] : unwrap(r.data))),

	get: (articleId: string): Promise<Article> =>
		apiClient.get<{ data: Article }>(`/articles/${articleId}`).then((r) => unwrap(r.data)),

	create: (body: CreateArticleInput): Promise<Article> =>
		apiClient.post<{ data: Article }>('/articles', body).then((r) => unwrap(r.data)),

	update: (articleId: string, patch: UpdateArticleInput): Promise<Article> =>
		apiClient.put<{ data: Article }>(`/articles/${articleId}`, patch).then((r) => unwrap(r.data)),

	// 204, no body — resolves void, never attempts to unwrap a body.
	remove: (articleId: string): Promise<void> => apiClient.delete<void>(`/articles/${articleId}`).then(() => undefined),

	transition: (articleId: string, to: ArticleStatus): Promise<Article> =>
		apiClient.post<{ data: Article }>(`/articles/${articleId}/transition`, { to }).then((r) => unwrap(r.data)),

	availableActions: (articleId: string): Promise<readonly ArticleStatus[]> =>
		apiClient
			.get<{ data: readonly ArticleStatus[] }>(`/articles/${articleId}/available-actions`)
			.then((r) => unwrap(r.data)),

	facets: (axisCode: string, params: ArticleListParams = {}): Promise<FacetValue[]> =>
		apiClient
			.get<{ data: FacetValue[] }>(`/articles/facets/${axisCode}`, { params: articleListQueryParams(params) })
			.then((r) => unwrap(r.data)),

	/**
	 * The article's translation group as a locale-coverage projection — every live
	 * locale variant, exactly one of which is the source, and no article bodies.
	 *
	 * Resolves the group from any member, so a translated variant's own id answers
	 * the same group as its source's. Normalizes an empty response to `[]` for the
	 * same reason `list` does.
	 */
	listTranslations: (articleId: string): Promise<ArticleTranslationVariant[]> =>
		apiClient
			.get<{ data: ArticleTranslationVariant[] } | ''>(`/articles/${articleId}/translations`)
			.then((r) => (r.status === 204 || !r.data ? [] : unwrap(r.data))),

	/**
	 * Request an AI translation of a SOURCE article into one or more target
	 * locales, in one call. Returns the created-or-reset target rows, already at
	 * `translationStatus: 'pending'` — the translated text itself lands later,
	 * written back by the worker.
	 */
	requestTranslation: (articleId: string, targetLocales: string[]): Promise<Article[]> =>
		apiClient
			.post<{ data: Article[] }>(`/articles/${articleId}/translations`, { targetLocales })
			.then((r) => unwrap(r.data)),
}
